import { findAnswerEmailUseCase } from '@src/main/modules/other_incidents/application/use-cases/send/send-incident-email.usecase';
import {
  Incident,
  IncidentCriteria,
} from '@src/main/modules/other_incidents/domain/incident.model';
import { OtherIncidentFilterRequestDto } from '@src/main/modules/other_incidents/infrastructure/dto/other-incident-filter.dto';
import { IncidentsRepository } from '@src/main/modules/other_incidents/infrastructure/incidents.repository';
import { createOtherIncidentHttpRepository } from '@src/main/modules/other_incidents/infrastructure/OtherIncidentHttpRepository';
import { findAllIncidents } from '@src/main/modules/other_incidents/ui/hooks/find-all-otherincident.usecase';
import { IncidentFilterMeta } from '@src/main/modules/other_incidents/ui/hooks/mapIncidentFiltersToCriteria';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useIncidents = (
  initialFilters: IncidentFilterMeta,
  mapFiltersToCriteria: (
    filters: IncidentFilterMeta,
    emptyCriteria: IncidentCriteria,
  ) => IncidentCriteria,
  emptyCriteria: IncidentCriteria,
) => {
  const { user } = useAuth();

  const [filters, setFilters] = useState<IncidentFilterMeta>(initialFilters);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('flightDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  const repository = useMemo<IncidentsRepository | null>(
    () => (user?.token ? createOtherIncidentHttpRepository(tokenProvider) : null),
    [tokenProvider, user?.token],
  );

  const resetPage = useCallback(() => setPageNumber(0), []);

  const criteria = useMemo(() => {
    const base = mapFiltersToCriteria(filters, emptyCriteria);

    return {
      ...base,
      startIncidentDate: base.startIncidentDate?.toISOString(),
      endIncidentDate: base.endIncidentDate?.toISOString(),
      offset: pageNumber * pageSize,
      limit: pageSize,
      sortField,
      sortOrder,
    };
  }, [filters, emptyCriteria, pageNumber, pageSize, sortField, sortOrder, mapFiltersToCriteria]);

  const { data, isLoading, refetch, isFetching } = useQuery<PageResponse<Incident>>({
    queryKey: ['incidents', criteria],
    queryFn: async () => {
      if (!repository) {
        throw new Error('Repository not available');
      }

      const incidentsPage = await findAllIncidents(repository, criteria);

      const items = await Promise.all(
        incidentsPage.items.map(async (incident) => {
          try {
            if (!incident.idEmployeeAnswered) {
              return {
                ...incident,
                message: '',
              };
            }

            const message = await findAnswerEmailUseCase(
              tokenProvider,
              `${incident.flightNumber}`,
              incident.incidentDate,
              incident.employeeEmail,
            );

            return {
              ...incident,
              message,
            };
          } catch {
            return {
              ...incident,
              message: '',
            };
          }
        }),
      );

      return {
        ...incidentsPage,
        items,
      };
    },
    enabled: !!repository,
    placeholderData: (prev) => prev,
  });

  const handlePageChange = useCallback((e: DataTablePageEvent) => {
    setPageNumber(Math.floor(e.first / e.rows));
    setPageSize(e.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'flightDate');
    setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc');
  }, []);

  return {
    filters,
    setFilters,

    datos: data?.items ?? [],
    total: data?.total ?? 0,

    isLoading,
    isFetching,

    pageNumber,
    pageSize,

    handlePageChange,
    handleSort,
    resetPage,

    sortField,
    sortOrder,

    refetchReports: refetch,
  };
};

export const useIncidentsRepos = () => {
  const { user } = useAuth();
  const [repository, setRepository] = useState<IncidentsRepository | null>(null);

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);

  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  useEffect(() => {
    if (user?.token) {
      setRepository(createOtherIncidentHttpRepository(tokenProvider));
    } else {
      setRepository(null);
    }
  }, [user?.token, tokenProvider]);

  return {
    repository,
    isReady: !!repository,
  };
};

export const useExportExcel = (repository?: IncidentsRepository | null) =>
  useMutation({
    mutationFn: async (request: OtherIncidentFilterRequestDto) => {
      if (!repository) {
        throw new Error('Repository not available');
      }

      const response = await repository.findAllExport(request);

      return response;
    },
  });
